import React from "react";
import { createContext, useEffect, useState } from "react";
import { AdminPanelTopic, Scraper } from "../../components/TopicsPanel/types";
import { getAllScrapers, getAllTopics } from "../../services/fetch";
import { useLabelHandlers } from "./useLabelHandlers";
import { AdminPanelProps, IConfigProvider } from "./types";
import { useScraperHandlers } from "./useScraperHandlers";
import { useTopicHandlers } from "./useTopicHandlers";

const AdminPanelContext = createContext({} as IConfigProvider);

const AdminPanelProvider: React.FunctionComponent<AdminPanelProps> = ({
	children,
}) => {
	const [topics, setTopics] = useState<AdminPanelTopic[]>();
	const [scrapers, setScrapers] = useState<Scraper[]>();
	const [selectedTopic, setSelectedTopic] = useState<number>(null);
	const [selectedConfigId, setSelectedConfigId] = useState(null);
	const [generatedSearchTerms, setGeneratedSearchTerms] = useState<string[]>(
		[]
	);
	const { handleLabels, handleLabelValues } = useLabelHandlers(
		topics,
		setTopics,
		selectedTopic
	);
	const { handleScrapers, handleConfigs, handleConfigOptionValues } =
		useScraperHandlers(topics, setTopics, selectedTopic);
	const { handleTopics } = useTopicHandlers(setTopics);

	useEffect(() => {
		setGeneratedSearchTerms([]);
	}, [selectedConfigId]);

	useEffect(() => {
		const fetchTopics = async () => {
			try {
				const data = await getAllTopics();
				setTopics(data.result);
			} catch (error) {
				console.error("Failed to fetch Topics for admin panel:", error);
			}
		};
		const fetchScrapers = async () => {
			try {
				const data = await getAllScrapers();
				setScrapers(data.result);
			} catch (error) {
				console.error("Failed to fetch Scrapers for admin panel:", error);
			}
		};
		fetchTopics();
		fetchScrapers();
	}, []);

	const adminContextValue = {
		topics,
		scrapers,
		selectedTopic,
		selectedConfigId,
		generatedSearchTerms,
		setGeneratedSearchTerms,
		setSelectedTopic,
		setTopics,
		setSelectedConfigId,
		handleTopics,
		handleLabels,
		handleLabelValues,
		handleScrapers,
		handleConfigs,
		handleConfigOptionValues,
	};

	return (
		<AdminPanelContext.Provider value={adminContextValue}>
			{children}
		</AdminPanelContext.Provider>
	);
};

const useAdminPanelContext = () => {
	const context = React.useContext(AdminPanelContext);
	if (context === undefined) {
		throw new Error(
			"useAdminPanelContext must be used within a AdminPanelProvider"
		);
	}
	return context;
};

export { AdminPanelProvider, useAdminPanelContext };
